import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, FileCheck, AlertCircle, CheckCircle, XCircle, Lightbulb, Target } from "lucide-react";
import { Button } from "../../components/ui/button";

const EviterErreursAppelOffresArticle = () => {
  return (
    <>
      <Helmet>
        <title>10 Erreurs à Éviter dans un Appel d'Offres | Mon DCE Express</title>
        <meta name="description" content="Découvrez les 10 erreurs les plus courantes qui font échouer les candidatures aux marchés publics et comment les éviter. Guide pratique pour maximiser vos chances." />
        <meta name="keywords" content="erreurs appel d'offres, candidature marché public, échec appel d'offres, conseils candidature, dossier rejeté" />
        <link rel="canonical" href="https://www.dce-express.fr/blog/eviter-erreurs-appel-offres" />
      </Helmet>

      <article className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/blog" className="inline-flex items-center text-amber-100 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Guide Pratique
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Candidature
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              10 Erreurs Fatales à Éviter dans vos Appels d'Offres
            </h1>
            
            <p className="text-xl text-amber-100 mb-6 leading-relaxed">
              Les erreurs les plus courantes qui font échouer les candidatures et comment les éviter 
              pour maximiser vos chances de remporter des marchés publics.
            </p>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-amber-100">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Avril 2025
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                10 min de lecture
              </span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Chaque année, des milliers de candidatures sont rejetées pour des erreurs évitables. 
              Après avoir analysé des centaines de dossiers, voici les 10 erreurs les plus fréquentes 
              et nos conseils pour les éviter.
            </p>

            {/* Error 1 */}
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-red-600">1</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-red-800 mb-2">Ne pas lire le règlement de consultation en entier</h2>
                  <p className="text-red-900 mb-4">
                    C'est l'erreur n°1. Le RC contient toutes les règles du jeu : critères de sélection, 
                    documents exigés, format de réponse, date limite précise...
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-red-200">
                    <p className="text-sm text-red-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Utilisez une checklist et surlignez les points importants
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 2 */}
            <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-orange-600">2</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-orange-800 mb-2">Dépasser la date limite de dépôt</h2>
                  <p className="text-orange-900 mb-4">
                    Même une minute de retard = candidature irrecevable. Les plateformes ferment 
                    automatiquement à l'heure précise indiquée.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-orange-200">
                    <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Déposez 48h avant la date limite pour gérer les imprévus techniques
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 3 */}
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-amber-600">3</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-amber-800 mb-2">Oublier des documents obligatoires</h2>
                  <p className="text-amber-900 mb-4">
                    DC1, DC2, attestations d'assurance, Kbis... Un seul document manquant et votre 
                    dossier peut être écarté ou régularisé (perte de temps).
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Créez un dossier type avec tous les documents à jour
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 4 */}
            <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-yellow-600">4</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-yellow-800 mb-2">Mémoire technique générique (copier-coller)</h2>
                  <p className="text-yellow-900 mb-4">
                    Les acheteurs repèrent immédiatement un mémoire copié-collé d'un autre marché. 
                    Chaque réponse doit être adaptée au contexte spécifique.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-yellow-200">
                    <p className="text-sm text-yellow-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Personnalisez chaque mémoire en reprenant les termes du DCE
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 5 */}
            <div className="bg-lime-50 border-l-4 border-lime-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-lime-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-lime-600">5</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-lime-800 mb-2">Mal calculer son prix</h2>
                  <p className="text-lime-900 mb-4">
                    Prix trop bas = suspicion de dumping, demande de justification. Prix trop haut = 
                    élimination au classement. L'équilibre est crucial.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-lime-200">
                    <p className="text-sm text-lime-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Analysez les prix des marchés similaires passés (données ouvertes)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 6 */}
            <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-green-600">6</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-green-800 mb-2">Ignorer les critères de notation</h2>
                  <p className="text-green-900 mb-4">
                    Si le critère technique vaut 60% et le prix 40%, concentrez vos efforts sur la 
                    qualité technique. Chaque critère doit être traité proportionnellement.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Structurez votre mémoire selon les critères et leur pondération
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 7 */}
            <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-teal-600">7</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-teal-800 mb-2">Ne pas répondre aux questions posées</h2>
                  <p className="text-teal-900 mb-4">
                    Le DCE pose des questions précises. Répondez exactement à ce qui est demandé, 
                    pas à côté. Les hors-sujet sont pénalisés.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-teal-200">
                    <p className="text-sm text-teal-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Reprenez chaque question du CCTP point par point
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 8 */}
            <div className="bg-cyan-50 border-l-4 border-cyan-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-cyan-600">8</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-cyan-800 mb-2">Présentation négligée</h2>
                  <p className="text-cyan-900 mb-4">
                    Fautes d'orthographe, mise en page chaotique, documents scannés de travers... 
                    La forme compte autant que le fond pour l'image professionnelle.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-cyan-200">
                    <p className="text-sm text-cyan-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Faites relire par un tiers et utilisez des modèles professionnels
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 9 */}
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-blue-600">9</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-blue-800 mb-2">Références inadaptées ou insuffisantes</h2>
                  <p className="text-blue-900 mb-4">
                    Les références doivent être récentes (5 ans max), similaires au marché visé, 
                    et vérifiables. Des références sans rapport ne servent à rien.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Sélectionnez 3-5 références pertinentes avec attestations de bonne exécution
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error 10 */}
            <div className="bg-violet-50 border-l-4 border-violet-500 rounded-r-xl p-6 my-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-violet-600">10</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-violet-800 mb-2">Ne pas poser de questions en cas de doute</h2>
                  <p className="text-violet-900 mb-4">
                    Si quelque chose n'est pas clair dans le DCE, posez une question via la plateforme. 
                    Les réponses sont partagées à tous et clarifient les ambiguïtés.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-violet-200">
                    <p className="text-sm text-violet-800 font-medium flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Solution : Posez vos questions au moins 10 jours avant la date limite
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Checklist */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-2xl p-8 my-12">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                <FileCheck className="w-6 h-6" />
                Checklist avant dépôt
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  RC lu intégralement
                </label>
                <label className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Tous documents présents
                </label>
                <label className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Mémoire personnalisé
                </label>
                <label className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Prix vérifié et cohérent
                </label>
                <label className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Critères tous adressés
                </label>
                <label className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Relecture effectuée
                </label>
                <label className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  48h avant date limite
                </label>
                <label className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Accusé de réception conservé
                </label>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 text-center text-white mt-12">
            <h3 className="text-2xl font-bold mb-4">
              Analysez vos DCE automatiquement
            </h3>
            <p className="text-amber-100 mb-6 max-w-2xl mx-auto">
              Mon DCE Express détecte les points importants de vos appels d'offres 
              pour ne rien oublier.
            </p>
            <Link to="/analyse-gratuite">
              <Button size="lg" className="bg-white text-amber-600 hover:bg-amber-50">
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default EviterErreursAppelOffresArticle;
